const SCRIPT_ID = 'tr-google-translate-script'
const CONTAINER_ID = 'tr-google-translate-element'
const CALLBACK_NAME = '__trGoogleTranslateInit'
const COMBO_SELECTOR = '.goog-te-combo'

let googleInitPromise = null

const createHiddenTranslateContainer = () => {
  if (typeof document === 'undefined') return
  if (document.getElementById(CONTAINER_ID)) return

  const container = document.createElement('div')
  container.id = CONTAINER_ID
  container.style.position = 'fixed'
  container.style.left = '-9999px'
  container.style.bottom = '0'
  container.style.width = '1px'
  container.style.height = '1px'
  container.style.overflow = 'hidden'
  container.setAttribute('aria-hidden', 'true')
  document.body.appendChild(container)
}

const waitForTranslateCombo = (timeoutMs = 7000) =>
  new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is unavailable'))
      return
    }

    const startedAt = Date.now()
    const tick = () => {
      const combo = document.querySelector(COMBO_SELECTOR)
      if (combo) {
        resolve(combo)
        return
      }

      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error('Google Translate control did not initialize in time'))
        return
      }

      window.setTimeout(tick, 120)
    }

    tick()
  })

export const initializeGoogleTranslate = includedLanguages => {
  if (typeof window === 'undefined') return Promise.resolve()

  if (googleInitPromise) return googleInitPromise

  googleInitPromise = new Promise((resolve, reject) => {
    const initWidget = () => {
      try {
        if (!window.google?.translate?.TranslateElement) {
          throw new Error('Google Translate widget not available')
        }

        createHiddenTranslateContainer()

        if (!window.__trGoogleTranslateWidgetReady) {
          // eslint-disable-next-line no-new
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: includedLanguages.join(','),
              autoDisplay: false,
              layout:
                window.google.translate.TranslateElement.InlineLayout.SIMPLE
            },
            CONTAINER_ID
          )
          window.__trGoogleTranslateWidgetReady = true
        }

        waitForTranslateCombo()
          .then(() => resolve())
          .catch(reject)
      } catch (error) {
        reject(error)
      }
    }

    if (window.google?.translate?.TranslateElement) {
      initWidget()
      return
    }

    window[CALLBACK_NAME] = initWidget

    const existingScript = document.getElementById(SCRIPT_ID)
    if (existingScript) return

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `https://translate.google.com/translate_a/element.js?cb=${CALLBACK_NAME}`
    script.async = true
    script.onerror = () =>
      reject(new Error('Failed to load Google Translate script'))
    document.body.appendChild(script)
  }).catch(error => {
    // Allow retries in case script/network was blocked on a previous attempt.
    googleInitPromise = null
    throw error
  })

  return googleInitPromise
}

const getBaseDomain = hostname => {
  const hostnameParts = hostname.split('.')
  return hostnameParts.length >= 2
    ? `.${hostnameParts.slice(-2).join('.')}`
    : undefined
}

const getGoogTransCookie = () => {
  if (typeof document === 'undefined') return ''
  const value = document.cookie
    .split('; ')
    .find(cookiePart => cookiePart.startsWith('googtrans='))
  return value ? decodeURIComponent(value.split('=')[1] || '') : ''
}

const clearGoogTransCookies = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  const hostname = window.location.hostname
  const baseDomain = getBaseDomain(hostname)
  const domains = [undefined, hostname, baseDomain].filter(Boolean)
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT'

  document.cookie = `googtrans=;path=/;expires=${expires}`
  domains.forEach(domain => {
    document.cookie = `googtrans=;path=/;domain=${domain};expires=${expires}`
  })
}

const setGoogTransCookie = languageCode => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  const cookieValue = `/en/${languageCode}`
  const hostname = window.location.hostname
  const baseDomain = getBaseDomain(hostname)

  document.cookie = `googtrans=${cookieValue};path=/`
  if (baseDomain) {
    document.cookie = `googtrans=${cookieValue};path=/;domain=${baseDomain}`
  }
}

const changeComboLanguage = async languageCode => {
  const combo = await waitForTranslateCombo()
  const matchingOption = Array.from(combo.options || []).find(
    option => option.value === languageCode
  )
  if (!matchingOption) return false
  if (combo.value === languageCode) return true

  combo.value = languageCode
  combo.dispatchEvent(new Event('change', { bubbles: true }))
  return true
}

export const applyGoogleTranslateLanguage = async ({
  languageCode,
  includedLanguages
}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  // English is the source language for the site.
  const targetLanguage = languageCode || 'en'
  document.documentElement.lang = targetLanguage

  if (targetLanguage === 'en') {
    const previousGoogTrans = getGoogTransCookie()
    const wasTranslated =
      previousGoogTrans &&
      !previousGoogTrans.endsWith('/en') &&
      !previousGoogTrans.endsWith('/auto/en')

    clearGoogTransCookies()

    try {
      const changedToEnglish = await changeComboLanguage('en')
      if (!changedToEnglish) {
        await changeComboLanguage('')
      }
    } catch (error) {
      // No-op: if widget is not loaded yet, cookie + html lang already reset.
    }

    // Google Translate mutates DOM in-place; reloading ensures original English
    // content is restored consistently after switching back from translations.
    if (wasTranslated && typeof window !== 'undefined') {
      window.setTimeout(() => {
        window.location.reload()
      }, 60)
    }
    return
  }

  setGoogTransCookie(targetLanguage)
  await initializeGoogleTranslate(includedLanguages)
  await changeComboLanguage(targetLanguage)
}
